import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, IconButton, Tooltip, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ModalForms from '../../Shared/Modals/index.js';
import EntityDropdown from '../../Shared/EntityDropdown/EntityDropdown.tsx';
import OrganisationDropdown from '../../Shared/OrganisationDropdown/index.tsx';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { EntityActions } from '../../../Services/_actions/Entity/entity.actions.ts';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { entityPreviewDataAtom, userInformationAtom, entityFormsStateAtomFamily } from '../../../Services/_states/index.js';

type ErrorsMap = { [key: number]: any };

interface ControlledEntityDropdownProps {
  uniqueId: string;
  organisation_id: string;
  isSuperUser: boolean;
  value: any;
  onChange: (val: any) => void;
  label?: any;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
}

const ControlledEntityDropdown: React.FC<ControlledEntityDropdownProps> = ({
  uniqueId,
  organisation_id,
  isSuperUser,
  ...props
}) => {
  const setTableState = useSetRecoilState(entityFormsStateAtomFamily(uniqueId));
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (organisation_id) {
      setTableState((prev: any) => {
        if (prev?.filters?.organisation_id === organisation_id) {
          return prev;
        }
        return {
          ...prev,
          filters: {
            ...prev?.filters,
            organisation_id: organisation_id,
            page: 1,
          },
        };
      });
      setIsReady(true);
    } else {
      setIsReady(false);
    }
  }, [uniqueId, organisation_id, setTableState]);

  if (!organisation_id) {
    return (
      <SearchableDropdown
        disabled
        label={isSuperUser ? 'Select Organisation First' : 'Select Entity'}
        options={[]}
        value={null}
      />
    );
  }

  if (!isReady) {
    return (
      <SearchableDropdown
        disabled
        loading
        label="Select Entity"
        options={[]}
        value={props.value || null}
      />
    );
  }

  return (
    <EntityDropdown
      uniqueId={uniqueId}
      organisation_id={organisation_id}
      {...props}
    />
  );
};

interface EntitySettingsModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;

  /** Which attribute is being edited + its index */
  attribute: any;
  index: number;

  /** Parent state & helpers */
  errors: ErrorsMap;
  leadsFormItems: any[];
  // entityPreviewData: any;
  // setEntityPreviewData: (v: any) => void;
  handleAttributeChange: (updatedAttr: any, index: number) => void;
  handleAddFieldMapping: (attrIndex: number) => void;

  /** Utility from parent (kept here to avoid duplicate logic) */
  modifyEntityFormFieldsArray: (list: any[]) => Array<{ name: string; label: string }>;
}

const EntitySettingsModal: React.FC<EntitySettingsModalProps> = ({
  open,
  onClose,
  onSave,
  attribute,
  index,
  errors,
  leadsFormItems,
  // entityPreviewData,
  // setEntityPreviewData,
  handleAttributeChange,
  handleAddFieldMapping,
  modifyEntityFormFieldsArray,
}) => {
  const entityId = attribute?.entity?._id;
  const entityActions = EntityActions();
  const [entityPreviewData, setEntityPreviewData] = useRecoilState(entityPreviewDataAtom);

  const loggedInUser = useRecoilValue(userInformationAtom);
  const isSuperUser = !!loggedInUser?.is_superuser;
  const loggedInOrgId = loggedInUser?.profile?.organisation?.organisation_id || '';

  const [selectedOrg, setSelectedOrg] = useState<any>(attribute?.organisation || null);

  useEffect(() => {
    if (open) {
      setSelectedOrg(attribute?.organisation || null);
    }
  }, [open, attribute?.organisation]);

  const effectiveOrgId = isSuperUser
    ? (selectedOrg?.organisation_id || attribute?.organisation?.organisation_id || '')
    : loggedInOrgId;

  const handleOrgChange = (org: any) => {
    const newOrgId = org?.organisation_id || '';
    const currentOrgId = selectedOrg?.organisation_id || attribute?.organisation?.organisation_id || '';
    const newOrgObj = org ? { organisation_id: org?.organisation_id || '', name: org?.name || '' } : null;

    setSelectedOrg(newOrgObj);

    if (newOrgId !== currentOrgId) {
      handleAttributeChange(
        {
          ...attribute,
          organisation: newOrgObj,
          entity: null,
          selectedEntityFields: [],
          query_parameter: [{ param: '', matched_with: '' }],
        },
        index
      );
    }
  };

  const getEntityFormById = async (entity) => {
    try {
      // if (!entity?._id) return;
      // if (entityPreviewData?.[entity?._id]) return;
      const response = await entityActions?.getFormById(entity?._id);
      if (response?.status === 200 && response?.data?.items) {
        const form = response?.data?.items;
        setEntityPreviewData({ ...entityPreviewData, [entity?._id]: form });
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (attribute?.entity?._id) {
      getEntityFormById(attribute?.entity);
    }
  }, [attribute?.entity]);

  const paramOptions = [
    { alias_name: 'global_phone_number', key_name: 'global_phone_number' },
    ...leadsFormItems.filter((_, i) => i !== 0 && i !== index && i! <= index),
  ];

  const body = (
    <>
      {isSuperUser && (
        <Box sx={{ mt: 1 }}>
          <OrganisationDropdown
            label="Select Organisation"
            value={selectedOrg}
            onChange={handleOrgChange}
            autoSelectValue={false}
          />
        </Box>
      )}

      <Box sx={{ mt: 1 }}>
        <ControlledEntityDropdown
          key={effectiveOrgId || 'none'}
          uniqueId={`entity_modal_${effectiveOrgId || 'default'}`}
          organisation_id={effectiveOrgId}
          isSuperUser={isSuperUser}
          value={attribute?.entity || null}
          onChange={(val: any) => {
            handleAttributeChange(
              {
                ...attribute,
                ...(isSuperUser && selectedOrg ? { organisation: selectedOrg } : {}),
                entity: { _id: val?._id || '', name: val?.name || '' },
                selectedEntityFields: [],
                query_parameter: attribute.query_parameter?.length
                  ? attribute.query_parameter
                  : [{ param: '', matched_with: '' }],
              },
              index
            );
            if (val?._id) {
              setEntityPreviewData({ ...entityPreviewData, [val?._id]: val ?? null });
            }
          }}
          label="Select Entity"
          error={!!errors[index]?.entity}
          errorMessage={errors[index]?.entity || ''}
        />
      </Box>

      {entityId && (entityPreviewData[entityId]?.form_fields?.length ?? 0) > 0 && (
        <Box sx={{ mt: 2 }}>
          {attribute.query_parameter?.map((mapping: any, mapIndex: number) => (
            <Grid container spacing={2} key={`mapping-${mapIndex}`} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <SearchableDropdown
                  label="Select Param"
                  options={paramOptions}
                  labelKey="key_name"
                  valueKey="alias_name"
                  value={
                    mapping.param
                      ? paramOptions.find(
                        (option: any) =>
                          option.alias_name === mapping.param || option.key_name === mapping.param
                      ) ?? null
                      : null
                  }
                  onChange={(selected) => {
                    const updatedMappings = [...(attribute.query_parameter || [])];
                    updatedMappings[mapIndex] = {
                      ...updatedMappings[mapIndex],
                      param: selected?.alias_name || '',
                      entityId: selected?.entity?._id ?? '',
                    };
                    if (selected?.key_type !== 'ENTITY') {
                      delete updatedMappings[mapIndex]['entity_form_field'];
                      delete updatedMappings[mapIndex]['entityId'];
                    }
                    handleAttributeChange(
                      { ...attribute, query_parameter: updatedMappings },
                      index
                    );
                  }}
                />
              </Grid>

              {mapping?.entityId &&
                modifyEntityFormFieldsArray(
                  entityPreviewData?.[mapping?.entityId]?.form_fields ?? []
                ).length > 0 && (
                  <Grid item xs={6}>
                    <SearchableDropdown
                      options={modifyEntityFormFieldsArray(
                        entityPreviewData?.[mapping?.entityId]?.form_fields ?? []
                      )}
                      value={
                        mapping?.entity_form_field
                          ? entityPreviewData[mapping?.entityId]?.form_fields?.find(
                            (f: any) => f.name === mapping?.entity_form_field
                          )
                          : null
                      }
                      onChange={(selected) => {
                        const updatedMappings = [...(attribute.query_parameter || [])];
                        updatedMappings[mapIndex] = {
                          ...updatedMappings[mapIndex],
                          entity_form_field: selected?.name || '',
                        };
                        handleAttributeChange(
                          { ...attribute, query_parameter: updatedMappings },
                          index
                        );
                      }}
                      label={
                        <Typography>
                          Select Previous Node Entity Form Field{' '}
                          <span style={{ color: 'red' }}>*</span>
                        </Typography>
                      }
                    />
                  </Grid>
                )}

              <Grid item xs={6}>
                <SearchableDropdown
                  label="Select Form Field"
                  options={entityPreviewData[entityId]?.form_fields ?? []}
                  valueKey="name"
                  value={
                    mapping.matched_with
                      ? entityPreviewData[entityId]?.form_fields?.find(
                        (f: any) => f.name === mapping?.matched_with
                      )
                      : null
                  }
                  onChange={(selected) => {
                    const updatedMappings = [...(attribute.query_parameter || [])];
                    updatedMappings[mapIndex] = {
                      ...updatedMappings[mapIndex],
                      matched_with: selected?.name || '',
                    };
                    handleAttributeChange(
                      { ...attribute, query_parameter: updatedMappings },
                      index
                    );
                  }}
                />
              </Grid>

              <Grid item xs={2}>
                {attribute.query_parameter?.length > 1 && (
                  <Tooltip title="Remove Mapping">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => {
                        const updatedMappings = [...(attribute.query_parameter || [])];
                        updatedMappings.splice(mapIndex, 1);
                        handleAttributeChange(
                          { ...attribute, query_parameter: updatedMappings },
                          index
                        );
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>
            </Grid>
          ))}

          <IconButton color="primary" onClick={() => handleAddFieldMapping(index)}>
            <AddIcon />
          </IconButton>

          <Grid mt={2}>
            <SearchableDropdown sx={{ '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                  top: '-3.3px',
                },}}
              options={modifyEntityFormFieldsArray(entityPreviewData[entityId]?.form_fields ?? [])}
              // value={
              //   (entityPreviewData[entityId]?.form_fields ?? [])?.filter((field: any) =>
              //     attribute.selectedEntityFields?.includes(field?.name)
              //   ) ?? []
              // }
              value={(attribute.selectedEntityFields || []).map((fieldName: string) =>
                (entityPreviewData[entityId]?.form_fields ?? []).find(
                  (f: any) => f.name === fieldName
                )
              )}
              onChange={(e) => {
                const updateArr = (e || []).map((i: any) => i.name);
                handleAttributeChange({ ...attribute, selectedEntityFields: updateArr }, index);
              }}
              label={
                <Typography>
                  Retrieve Entity Field <span style={{ color: 'red' }}>*</span>
                </Typography>
              }
              multiSelect
              valueKey="name"
              getOptionDisabled={() => (attribute?.selectedEntityFields ?? [])?.length > 1}
            />
          </Grid>
        </Box>
      )}

      {entityId && (entityPreviewData[entityId]?.form_fields?.length ?? 0) === 0 && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
          Selected entity has no form fields defined.
        </Typography>
      )}
    </>
  );

  return (
    <ModalForms
      show={open}
      handleClose={onClose}
      modalHeader="Entity Data Setting"
      size="lg"
      modalBody={body}
      modalFooter={
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={onClose} size="small">
            Close
          </Button>
          <Button variant="contained" onClick={onSave || onClose} size="small">
            Save
          </Button>
        </Box>
      }
    />
  );
};

export default EntitySettingsModal;
